import * as React from 'react';
import moment from 'moment-timezone';
import { Box, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import axios from 'axios';
import clsx from 'clsx';
import lodashGet from 'lodash/get';

import config from 'config';
import { PricePanel } from 'components/DateFormPanel/PricePanel';
import { PromotionPanel } from 'components/DateFormPanel/PromotionPanel';
import { Modal } from 'components/Modal/Modal';
import { ReduxState } from 'ducks';
import { selectCandidateProductInstances, selectGuestTypes } from 'lib/util/guestSelectUtils';
import { formattedTotalBookedText } from 'lib/util/formattedTotalBookedText';
import {
  getPrices,
  Price,
  PromotionSummary,
  toProductShape,
  toPromotionSummary,
} from 'lib/util/productShape';
import { selectShowTotalBooked } from 'lib/util/privateMarketplace';
import { sortCalendarUnit } from 'lib/util/sortCalendarUnit';
import { DateFormValues } from 'components/DateFormPanel/DateFormValues';
import { ProductPromotion, GuestType, Recurrence } from 'models/product';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import {
  getPerBookingMaximumParticipantsText,
  validatePerBookingParticipants,
} from 'lib/util/getEnablePerBookingMaximumParticipants';
import { getBookingStartDateTimeText } from 'lib/util/getBookingStartDateTimeText';
import { isStillNotOnSale } from 'lib/util/isStillNotOnSale';

import { GuestTypeList } from './GuestTypeList';
import { OtherParticipantsModal } from './OtherParticipantsModal';

type Props = FormRenderProps<DateFormValues> & {
  showPrices: boolean;
  loading?: boolean;
  lastProductInstanceId: string | null;
  hideDateRange?: boolean;
  buttonLabel?: string;
  shouldShowPricingOnCalendar?: boolean;
};

const getTotalGuestCount = (guestTypes: GuestType[], values: DateFormValues): number => {
  let total = 0;
  for (const guestType of guestTypes) {
    total += parseInt(String(lodashGet(values, `guestCount.${guestType.key}`) || '0'));
  }
  return total;
};

const isAvailablePeriod = (
  recurrence: Recurrence,
  timezone: string,
  bookingAllowedDaysBeforeParticipation: number | null
): boolean => {
  const now = moment().tz(timezone);
  const start = moment.tz(recurrence.start_date_local, timezone);
  const end = moment.tz(recurrence.end_date_local, timezone);

  if (bookingAllowedDaysBeforeParticipation !== null) {
    if (now.isBefore(start.subtract(bookingAllowedDaysBeforeParticipation, 'days'))) {
      return false;
    }
  }

  if (now.isAfter(end.add(1, 'day'))) {
    // TODO: Fix this
    return false;
  }

  return true;
};

export const DateFormFreeStartDateTime = ({
  handleSubmit,
  loading,
  invalid,
  showPrices,
  values,
  lastProductInstanceId,
  hideDateRange,
  buttonLabel,
  shouldShowPricingOnCalendar = false,
}: Props) => {
  const { t } = useTranslation();
  const router = useRouter();

  const { apiKey } = React.useContext(ApiKeyContext);

  const isUpgrade = Boolean(router.query.u);

  const [showOtherParticipantsModal, setShowOtherParticipantsModal] = React.useState(false);
  const [confirmationType, setConfirmationType] = React.useState<
    'REQUEST' | 'INSTANT' | 'REJECT' | null
  >(null);
  const { date, guestCount } = values;
  const guestCountTotal = guestCount
    ? Object.values(guestCount).reduce((a, b) => a + Number(b), 0)
    : 0;

  const product = useSelector((state: ReduxState) => state.server.product.product);

  const calendarUnitOrder = product?.booking_widget_settings?.calendar_unit_order ?? [];

  let guestTypes = useSelector((state: ReduxState) => selectGuestTypes(state, { t }));
  if (config.enableUnitDisplayOrder) {
    guestTypes = sortCalendarUnit([...guestTypes], calendarUnitOrder);
  }
  let defaultShowGuestCount = 3;
  if (config.enableUnitDisplayOrder) {
    if (calendarUnitOrder.some((g) => g.default_display_in_calendar)) {
      defaultShowGuestCount = 0;
      calendarUnitOrder.forEach((g) => {
        if (g.default_display_in_calendar) {
          defaultShowGuestCount++;
        }
      });
    }
  }

  const candidateProductInstances = useSelector((state: ReduxState) =>
    selectCandidateProductInstances(state, { date })
  );

  const productInstance = candidateProductInstances.find((p) => p.id === lastProductInstanceId);

  const shouldShowTotalBooked = useSelector(selectShowTotalBooked);

  const [showExpandedPromotionPrices, setShowExpandedPromotionPrices] = React.useState<boolean>(
    false
  );

  const isPerBookingParticipantRuleActive =
    product?.booking_widget_settings?.is_per_booking_participant_rule_active ?? false;
  const perBookingMinimumParticipants =
    product?.booking_widget_settings?.per_booking_participant_rule?.minimum_participants ?? 0;
  const perBookingMaximumParticipants =
    product?.booking_widget_settings?.per_booking_participant_rule?.maximum_participants ?? 0;
  const perBookingParticipantsText = getPerBookingMaximumParticipantsText(
    perBookingMinimumParticipants,
    perBookingMaximumParticipants,
    t
  );

  React.useEffect(() => {
    if (guestCount && Object.keys(guestCount).length > 0 && lastProductInstanceId != null) {
      setConfirmationType(null);

      if (!validatePerBookingParticipants(guestCountTotal, product)) {
        const searchParams = new URLSearchParams();

        const guestCounts: {
          guest_type_key: string;
          count: number;
        }[] = [];
        Object.keys(guestCount).forEach((key) => {
          guestCounts.push({
            guest_type_key: key,
            count: Number(guestCount[key]),
          });
        });
        searchParams.append('guest_counts', encodeURIComponent(JSON.stringify(guestCounts)));

        const url = `${
          config.apiUrl
        }/products/-/instances/${lastProductInstanceId}?${searchParams.toString()}`;

        axios
          .get(url, {
            headers: { 'x-api-key': apiKey },
          })
          .then((response) => {
            setConfirmationType(response.data.confirmation_type_for_given_guests ?? null);
          });
      }
    }
  }, [lastProductInstanceId, guestCount]);

  const productShape = product && toProductShape(product);
  let prices: Price[] =
    productShape?.priceSchedules?.find((priceSchedule) => priceSchedule.prices != null)?.prices ??
    [];
  let promotions: PromotionSummary[] = (productShape && productShape.promotionSummaries) || [];
  if (productInstance && productInstance.units && productInstance.units.length > 0) {
    prices = getPrices(
      productInstance.units,
      undefined,
      productInstance.dynamic_pricing_adjusted_unit_prices
    );
    promotions = productInstance.promotions.map((promo: ProductPromotion) =>
      toPromotionSummary(promo, productInstance.units)
    );
  }

  const totalBooked = productShape?.totalBooked;

  const isModal = Boolean(router.query.isModal);

  if (!product) return null;
  if (
    !product?.booking_widget_settings?.is_visible ||
    product?.booking_widget_settings?.is_view_only ||
    !isAvailablePeriod(
      product.recurrence[0],
      product.start_timezone,
      product.agent_guest_booking_period_settings?.booking_allowed_days_before_participation ?? null
    )
  ) {
    return (
      <div className="widget-calendar__main">
        <p style={{ color: 'red' }}>{t('Sorry, this product is currently not available.')}</p>
      </div>
    );
  }

  if (isStillNotOnSale(product)) {
    return (
      <div className="widget-calendar__main">
        <p style={{ color: 'red' }}>{getBookingStartDateTimeText(product, t)}</p>
      </div>
    );
  }

  const hiddenParticipantCount = getTotalGuestCount(
    guestTypes.slice(defaultShowGuestCount),
    values
  );

  return (
    <>
      <div className="widget-calendar__main">
        {isModal && (
          <div className={clsx('widget-calendar__block', 'product__name')}>
            <p className="widget-calendar__title">{t('Product')}</p>
            {product.product_name ?? ''}
          </div>
        )}
        {shouldShowTotalBooked && Boolean(totalBooked) && (
          <p className="widget-calendar__booking">{formattedTotalBookedText(totalBooked, t)}</p>
        )}
        <form noValidate={true} onSubmit={handleSubmit}>
          <div className="widget-calendar__block">
            <p className="widget-calendar__title">{t('Participants')}</p>
            {guestTypes.length > 0 ? (
              <GuestTypeList
                guestTypes={guestTypes.slice(0, defaultShowGuestCount)}
                className="widget-calendar__number"
              />
            ) : null}
            {guestTypes.length > defaultShowGuestCount && (
              <div
                className="widget-calendar__number"
                onClick={() => setShowOtherParticipantsModal(true)}
              >
                <p className="widget-calendar__number__display">
                  {hiddenParticipantCount > 0
                    ? t('Other Participants - Total {{count}}', {
                        count: hiddenParticipantCount,
                      })
                    : t('See other price units')}
                </p>
                <OtherParticipantsModal
                  open={showOtherParticipantsModal}
                  onClose={() => setShowOtherParticipantsModal(false)}
                  guestTypes={guestTypes.slice(defaultShowGuestCount)}
                />
              </div>
            )}
          </div>
          {loading && (
            <Box width="100%" position="absolute">
              <Box width="100%" display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            </Box>
          )}
          {isPerBookingParticipantRuleActive && (
            <p style={{ marginBottom: '8px' }}>{perBookingParticipantsText}</p>
          )}
          {!hideDateRange && (
            <span>
              {`${product?.recurrence[0].start_date_local} - ${product?.recurrence[0].end_date_local}`}
            </span>
          )}
          {isUpgrade && candidateProductInstances.length === 0 && (
            <p className="widget-calendar-error">{t('No availability for your date')}</p>
          )}
          <button
            disabled={
              invalid ||
              (guestTypes.length > 0 && guestCountTotal === 0) ||
              confirmationType === 'REJECT' ||
              (isUpgrade && confirmationType === 'REQUEST') ||
              validatePerBookingParticipants(guestCountTotal, product)
            }
            type="submit"
            className="widget-calendar__button"
          >
            {buttonLabel || t('Next')}
          </button>
        </form>
      </div>
      {showPrices &&
        (promotions.length > 0 ? (
          <PromotionPanel
            promotions={promotions}
            onClickExpanded={() => setShowExpandedPromotionPrices(true)}
          />
        ) : (
          <PricePanel prices={prices} shouldShowPricing={shouldShowPricingOnCalendar} />
        ))}
      {showExpandedPromotionPrices && (
        <Modal open onClose={() => setShowExpandedPromotionPrices(false)}>
          <PromotionPanel promotions={promotions} expanded={true} />
        </Modal>
      )}
    </>
  );
};
